import _classCallCheck from "/usr/src/app/www/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/usr/src/app/www/node_modules/@babel/runtime/helpers/esm/createClass";
import { useCollator } from "@react-aria/i18n";
import { mergeProps } from "@react-aria/utils";
import { focusSafely, getFocusableTreeWalker } from "@react-aria/focus";
import { useEffect, useRef, useMemo } from "react";
import _babelRuntimeHelpersEsmExtends from "@babel/runtime/helpers/esm/extends";
/**
 * Handles typeahead interactions with collections.
 */

export function useTypeSelect(options) {
  var keyboardDelegate = options.keyboardDelegate,
      selectionManager = options.selectionManager,
      onTypeSelect = options.onTypeSelect;
  var state = useRef({
    search: '',
    timeout: null
  }).current;

  var onKeyDown = function onKeyDown(e) {
    var character = $c78d7fa5f7d5832f9b4f97b33a679865$var$getStringForKey(e.key);

    if (!character || e.ctrlKey || e.metaKey) {
      return;
    } // Do not propagate the Spacebar event if it's meant to be part of the search.
    // When we time out, the search term becomes empty, hence the check on length.
    // Trimming is to account for the case of pressing the Spacebar more than once,
    // which should cycle through the selection/deselection of the focused item.


    if (character === ' ' && state.search.trim().length > 0) {
      e.preventDefault();
      e.stopPropagation();
    }

    state.search += character; // Use the delegate to find a key to focus.
    // Prioritize items after the currently focused item, falling back to searching the whole list.

    var key = keyboardDelegate.getKeyForSearch(state.search, selectionManager.focusedKey); // If no key found, search from the top.

    if (key == null) {
      key = keyboardDelegate.getKeyForSearch(state.search);
    }

    if (key != null) {
      selectionManager.setFocusedKey(key);

      if (onTypeSelect) {
        onTypeSelect(key);
      }
    }

    clearTimeout(state.timeout);
    state.timeout = setTimeout(function () {
      state.search = '';
    }, 500);
  };

  return {
    typeSelectProps: {
      // Using a capturing listener to catch the keydown event before
      // other hooks in order to handle the Spacebar event.
      onKeyDownCapture: keyboardDelegate.getKeyForSearch ? onKeyDown : null
    }
  };
}

function $c78d7fa5f7d5832f9b4f97b33a679865$var$getStringForKey(key) {
  // If the key is of length 1, it is an ASCII value.
  // Otherwise, if there are no ASCII characters in the key name,
  // it is a Unicode character.
  // See https://www.w3.org/TR/uievents-key/
  if (key.length === 1 || !/^[A-Z]/i.test(key)) {
    return key;
  }

  return '';
}

var $a9b9aa71af07c56ab1d89ca45381f4b$var$isMac = typeof window !== 'undefined' && window.navigator != null ? /^Mac/.test(window.navigator.platform) : false;

function $a9b9aa71af07c56ab1d89ca45381f4b$var$isCtrlKeyPressed(e) {
  if ($a9b9aa71af07c56ab1d89ca45381f4b$var$isMac) {
    return e.metaKey;
  }

  return e.ctrlKey;
}
/**
 * Handles interactions with selectable collections.
 */


export function useSelectableCollection(options) {
  var manager = options.selectionManager,
      delegate = options.keyboardDelegate,
      ref = options.ref,
      _options$autoFocus = options.autoFocus,
      autoFocus = _options$autoFocus === void 0 ? false : _options$autoFocus,
      _options$shouldFocusW = options.shouldFocusWrap,
      shouldFocusWrap = _options$shouldFocusW === void 0 ? false : _options$shouldFocusW,
      _options$disallowEmpt = options.disallowEmptySelection,
      disallowEmptySelection = _options$disallowEmpt === void 0 ? false : _options$disallowEmpt,
      _options$disallowSele = options.disallowSelectAll,
      disallowSelectAll = _options$disallowSele === void 0 ? false : _options$disallowSele,
      _options$selectOnFocu = options.selectOnFocus,
      selectOnFocus = _options$selectOnFocu === void 0 ? false : _options$selectOnFocu,
      _options$disallowType = options.disallowTypeAhead,
      disallowTypeAhead = _options$disallowType === void 0 ? false : _options$disallowType,
      shouldUseVirtualFocus = options.shouldUseVirtualFocus;

  var onKeyDown = function onKeyDown(e) {
    // Let child element (e.g. menu button) handle the event if the Alt key is pressed.
    // Keyboard events bubble through portals. Don't handle keyboard events
    // for elements outside the collection (e.g. menus).
    if (e.altKey || !ref.current.contains(e.target)) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        {
          if (delegate.getKeyBelow) {
            e.preventDefault();
            var nextKey = manager.focusedKey != null ? delegate.getKeyBelow(manager.focusedKey) : delegate.getFirstKey();

            if (nextKey != null) {
              manager.setFocusedKey(nextKey);

              if (manager.selectionMode === 'single' && selectOnFocus) {
                manager.replaceSelection(nextKey);
              }
            } else if (shouldFocusWrap) {
              var wrapKey = delegate.getFirstKey(manager.focusedKey);
              manager.setFocusedKey(wrapKey);

              if (manager.selectionMode === 'single' && selectOnFocus) {
                manager.replaceSelection(wrapKey);
              }
            }

            if (e.shiftKey && manager.selectionMode === 'multiple') {
              manager.extendSelection(nextKey);
            }
          }

          break;
        }

      case 'ArrowUp':
        {
          if (delegate.getKeyAbove) {
            e.preventDefault();

            var _nextKey = manager.focusedKey != null ? delegate.getKeyAbove(manager.focusedKey) : delegate.getLastKey();

            if (_nextKey != null) {
              manager.setFocusedKey(_nextKey);

              if (manager.selectionMode === 'single' && selectOnFocus) {
                manager.replaceSelection(_nextKey);
              }
            } else if (shouldFocusWrap) {
              var _wrapKey = delegate.getLastKey(manager.focusedKey);

              manager.setFocusedKey(_wrapKey);

              if (manager.selectionMode === 'single' && selectOnFocus) {
                manager.replaceSelection(_wrapKey);
              }
            }

            if (e.shiftKey && manager.selectionMode === 'multiple') {
              manager.extendSelection(_nextKey);
            }
          }

          break;
        }

      case 'ArrowLeft':
        {
          if (delegate.getKeyLeftOf) {
            e.preventDefault();

            var _nextKey2 = delegate.getKeyLeftOf(manager.focusedKey);

            if (_nextKey2 != null) {
              manager.setFocusedKey(_nextKey2);

              if (manager.selectionMode === 'single' && selectOnFocus) {
                manager.replaceSelection(_nextKey2);
              }
            }

            if (e.shiftKey && manager.selectionMode === 'multiple') {
              manager.extendSelection(_nextKey2);
            }
          }

          break;
        }

      case 'ArrowRight':
        {
          if (delegate.getKeyRightOf) {
            e.preventDefault();

            var _nextKey3 = delegate.getKeyRightOf(manager.focusedKey);

            if (_nextKey3 != null) {
              manager.setFocusedKey(_nextKey3);

              if (manager.selectionMode === 'single' && selectOnFocus) {
                manager.replaceSelection(_nextKey3);
              }
            }

            if (e.shiftKey && manager.selectionMode === 'multiple') {
              manager.extendSelection(_nextKey3);
            }
          }

          break;
        }

      case 'Home':
        if (delegate.getFirstKey) {
          e.preventDefault();
          var firstKey = delegate.getFirstKey(manager.focusedKey, $a9b9aa71af07c56ab1d89ca45381f4b$var$isCtrlKeyPressed(e));
          manager.setFocusedKey(firstKey);

          if (manager.selectionMode === 'single' && selectOnFocus) {
            manager.replaceSelection(firstKey);
          }

          if ($a9b9aa71af07c56ab1d89ca45381f4b$var$isCtrlKeyPressed(e) && e.shiftKey && manager.selectionMode === 'multiple') {
            manager.extendSelection(firstKey);
          }
        }

        break;

      case 'End':
        if (delegate.getLastKey) {
          e.preventDefault();
          var lastKey = delegate.getLastKey(manager.focusedKey, $a9b9aa71af07c56ab1d89ca45381f4b$var$isCtrlKeyPressed(e));
          manager.setFocusedKey(lastKey);

          if (manager.selectionMode === 'single' && selectOnFocus) {
            manager.replaceSelection(lastKey);
          }

          if ($a9b9aa71af07c56ab1d89ca45381f4b$var$isCtrlKeyPressed(e) && e.shiftKey && manager.selectionMode === 'multiple') {
            manager.extendSelection(lastKey);
          }
        }

        break;

      case 'PageDown':
        if (delegate.getKeyPageBelow) {
          e.preventDefault();

          var _nextKey4 = delegate.getKeyPageBelow(manager.focusedKey);

          if (_nextKey4 != null) {
            manager.setFocusedKey(_nextKey4);

            if (e.shiftKey && manager.selectionMode === 'multiple') {
              manager.extendSelection(_nextKey4);
            }
          }
        }

        break;

      case 'PageUp':
        if (delegate.getKeyPageAbove) {
          e.preventDefault();

          var _nextKey5 = delegate.getKeyPageAbove(manager.focusedKey);

          if (_nextKey5 != null) {
            manager.setFocusedKey(_nextKey5);

            if (e.shiftKey && manager.selectionMode === 'multiple') {
              manager.extendSelection(_nextKey5);
            }
          }
        }

        break;

      case 'a':
        if ($a9b9aa71af07c56ab1d89ca45381f4b$var$isCtrlKeyPressed(e) && manager.selectionMode === 'multiple' && disallowSelectAll !== true) {
          e.preventDefault();
          manager.selectAll();
        }

        break;

      case 'Escape':
        e.preventDefault();

        if (!disallowEmptySelection) {
          manager.clearSelection();
        }

        break;

      case 'Tab':
        {
          // There may be elements that are "tabbable" inside a collection (e.g. in a grid cell).
          // However, collections should be treated as a single tab stop, with arrow key navigation internally.
          // We don't control the rendering of these, so we can't override the tabIndex to prevent tabbing.
          // Instead, we handle the Tab key, and move focus manually to the first/last tabbable element
          // in the collection, so that the browser default behavior will apply starting from that element
          // rather than the currently focused one.
          if (e.shiftKey) {
            ref.current.focus();
          } else {
            var walker = getFocusableTreeWalker(ref.current, {
              tabbable: true
            });
            var next;
            var last;

            do {
              last = walker.lastChild();

              if (last) {
                next = last;
              }
            } while (last);

            if (next && !next.contains(document.activeElement)) {
              next.focus();
            }
          }

          break;
        }
    }
  };

  var onFocus = function onFocus(e) {
    if (manager.isFocused) {
      // If a focus event bubbled through a portal, reset focus state.
      if (!e.currentTarget.contains(e.target)) {
        manager.setFocused(false);
      }

      return;
    } // Focus events can bubble through portals. Ignore these events.


    if (!e.currentTarget.contains(e.target)) {
      return;
    }

    manager.setFocused(true);

    if (manager.focusedKey == null) {
      // If the user hasn't yet interacted with the collection, there will be no focusedKey set.
      // Attempt to detect whether the user is tabbing forward or backward into the collection
      // and either focus the first or last item accordingly.
      var relatedTarget = e.relatedTarget;

      if (relatedTarget && e.currentTarget.compareDocumentPosition(relatedTarget) & Node.DOCUMENT_POSITION_FOLLOWING) {
        var _manager$lastSelected;

        manager.setFocusedKey((_manager$lastSelected = manager.lastSelectedKey) != null ? _manager$lastSelected : delegate.getLastKey());
      } else {
        var _manager$firstSelecte;

        manager.setFocusedKey((_manager$firstSelecte = manager.firstSelectedKey) != null ? _manager$firstSelecte : delegate.getFirstKey());
      }
    }
  };

  var onBlur = function onBlur(e) {
    // Don't set blurred and then focused again if moving focus within the collection.
    if (!e.currentTarget.contains(e.relatedTarget)) {
      manager.setFocused(false);
    }
  };

  useEffect(function () {
    if (autoFocus) {
      var focusedKey = null; // Check focus strategy to determine which item to focus

      if (autoFocus === 'first') {
        focusedKey = delegate.getFirstKey();
      }

      if (autoFocus === 'last') {
        focusedKey = delegate.getLastKey();
      } // If there are any selected keys, make the first one the new focus target


      var selectedKeys = manager.selectedKeys;

      if (selectedKeys.size) {
        focusedKey = selectedKeys.values().next().value;
      }

      manager.setFocused(true);
      manager.setFocusedKey(focusedKey); // If no default focus key is selected, focus the collection itself.

      if (focusedKey == null && !shouldUseVirtualFocus) {
        focusSafely(ref.current);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  var handlers = {
    // We use a capturing listener to ensure that the keyboard events for the collection
    // override those of the children. For example, ArrowDown in a table should always go
    // to the cell below, and not open a menu.
    onKeyDownCapture: onKeyDown,
    onFocus: onFocus,
    onBlur: onBlur,
    onMouseDown: function onMouseDown(e) {
      // Prevent focus going to the collection when clicking on the scrollbar.
      e.preventDefault();
    }
  };

  var _useTypeSelect = useTypeSelect({
    keyboardDelegate: delegate,
    selectionManager: manager
  }),
      typeSelectProps = _useTypeSelect.typeSelectProps;

  if (!disallowTypeAhead) {
    handlers = mergeProps(typeSelectProps, handlers);
  } // If nothing is focused within the collection, make the collection itself tabbable.
  // This will be marshalled to either the first or last item depending on where focus came from.
  // If using virtual focus, don't set a tabIndex at all so that VoiceOver on iOS 14 doesn't try
  // to move real DOM focus to the element anyway.


  var tabIndex;

  if (!shouldUseVirtualFocus) {
    tabIndex = manager.focusedKey == null ? 0 : -1;
  }

  return {
    collectionProps: _babelRuntimeHelpersEsmExtends({}, handlers, {
      tabIndex: tabIndex
    })
  };
}
/**
 * Handles interactions with an item in a selectable collection.
 */

export function useSelectableItem(options) {
  var manager = options.selectionManager,
      key = options.key,
      ref = options.ref,
      shouldSelectOnPressUp = options.shouldSelectOnPressUp,
      isVirtualized = options.isVirtualized,
      shouldUseVirtualFocus = options.shouldUseVirtualFocus,
      focus = options.focus;

  var onSelect = function onSelect(e) {
    return manager.select(key, e);
  }; // Focus the associated DOM node when this item becomes the focusedKey


  var isFocused = key === manager.focusedKey;
  useEffect(function () {
    if (isFocused && manager.isFocused && !shouldUseVirtualFocus && document.activeElement !== ref.current) {
      if (focus) {
        focus();
      } else {
        focusSafely(ref.current);
      }
    }
  }, [ref, isFocused, manager.focusedKey, manager.isFocused, shouldUseVirtualFocus]); // Set tabIndex to 0 if the element is focused, or -1 otherwise so that only the last focused
  // item is tabbable.  If using virtual focus, don't set a tabIndex at all so that VoiceOver
  // on iOS 14 doesn't try to move real DOM focus to the item anyway.

  var itemProps = {};

  if (!shouldUseVirtualFocus) {
    itemProps = {
      tabIndex: isFocused ? 0 : -1,
      onFocus: function onFocus(e) {
        if (e.target === ref.current) {
          manager.setFocusedKey(key);
        }
      }
    };
  } // By default, selection occurs on pointer down. This can be strange if selecting an
  // item causes the UI to disappear immediately (e.g. menus).
  // If shouldSelectOnPressUp is true, we use onPressUp instead of onPressStart.
  // onPress requires a pointer down event on the same element as pointer up. For menus,
  // we want to be able to have the pointer down on the trigger that opens the menu and
  // the pointer up on the menu item rather than requiring a separate press.
  // For keyboard events, selection still occurs on key down.


  if (shouldSelectOnPressUp) {
    itemProps.onPressStart = function (e) {
      if (e.pointerType === 'keyboard') {
        onSelect(e);
      }
    };

    itemProps.onPressUp = function (e) {
      if (e.pointerType !== 'keyboard') {
        onSelect(e);
      }
    };
  } else {
    // On touch, it feels strange to select on touch down, so we special case this.
    itemProps.onPressStart = function (e) {
      if (e.pointerType !== 'touch') {
        onSelect(e);
      }
    };

    itemProps.onPress = function (e) {
      if (e.pointerType === 'touch') {
        onSelect(e);
      }
    };
  }

  if (!isVirtualized) {
    itemProps['data-key'] = key;
  }

  return {
    itemProps: itemProps
  };
}
/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

export var ListKeyboardDelegate = /*#__PURE__*/function () {
  function ListKeyboardDelegate(collection, disabledKeys, ref, collator) {
    _classCallCheck(this, ListKeyboardDelegate);

    this.collection = void 0;
    this.disabledKeys = void 0;
    this.ref = void 0;
    this.collator = void 0;
    this.collection = collection;
    this.disabledKeys = disabledKeys;
    this.ref = ref;
    this.collator = collator;
  }

  _createClass(ListKeyboardDelegate, [{
    key: "getKeyBelow",
    value: function getKeyBelow(key) {
      key = this.collection.getKeyAfter(key);

      while (key != null) {
        var item = this.collection.getItem(key);

        if (item.type === 'item' && !this.disabledKeys.has(key)) {
          return key;
        }

        key = this.collection.getKeyAfter(key);
      }
    }
  }, {
    key: "getKeyAbove",
    value: function getKeyAbove(key) {
      key = this.collection.getKeyBefore(key);

      while (key != null) {
        var item = this.collection.getItem(key);

        if (item.type === 'item' && !this.disabledKeys.has(key)) {
          return key;
        }

        key = this.collection.getKeyBefore(key);
      }
    }
  }, {
    key: "getFirstKey",
    value: function getFirstKey() {
      var key = this.collection.getFirstKey();

      while (key != null) {
        var item = this.collection.getItem(key);

        if (item.type === 'item' && !this.disabledKeys.has(key)) {
          return key;
        }

        key = this.collection.getKeyAfter(key);
      }
    }
  }, {
    key: "getLastKey",
    value: function getLastKey() {
      var key = this.collection.getLastKey();

      while (key != null) {
        var item = this.collection.getItem(key);

        if (item.type === 'item' && !this.disabledKeys.has(key)) {
          return key;
        }

        key = this.collection.getKeyBefore(key);
      }
    }
  }, {
    key: "getItem",
    value: function getItem(key) {
      return this.ref.current.querySelector("[data-key=\"" + key + "\"]");
    }
  }, {
    key: "getKeyPageAbove",
    value: function getKeyPageAbove(key) {
      var menu = this.ref.current;
      var item = this.getItem(key);

      if (!item) {
        return null;
      }

      var pageY = Math.max(0, item.offsetTop + item.offsetHeight - menu.offsetHeight);

      while (item && item.offsetTop > pageY) {
        key = this.getKeyAbove(key);
        item = this.getItem(key);
      }

      return key;
    }
  }, {
    key: "getKeyPageBelow",
    value: function getKeyPageBelow(key) {
      var menu = this.ref.current;
      var item = this.getItem(key);

      if (!item) {
        return null;
      }

      var pageY = Math.min(menu.scrollHeight, item.offsetTop - item.offsetHeight + menu.offsetHeight);

      while (item && item.offsetTop < pageY) {
        key = this.getKeyBelow(key);
        item = this.getItem(key);
      }

      return key;
    }
  }, {
    key: "getKeyForSearch",
    value: function getKeyForSearch(search, fromKey) {
      if (!this.collator) {
        return null;
      }

      var collection = this.collection;
      var key = fromKey || this.getFirstKey();

      while (key != null) {
        var item = collection.getItem(key);
        var substring = item.textValue.slice(0, search.length);

        if (item.textValue && this.collator.compare(substring, search) === 0) {
          return key;
        }

        key = this.getKeyBelow(key);
      }

      return null;
    }
  }]);

  return ListKeyboardDelegate;
}();
/**
 * Handles interactions with a selectable list.
 */

export function useSelectableList(props) {
  var selectionManager = props.selectionManager,
      collection = props.collection,
      disabledKeys = props.disabledKeys,
      ref = props.ref,
      keyboardDelegate = props.keyboardDelegate,
      autoFocus = props.autoFocus,
      shouldFocusWrap = props.shouldFocusWrap,
      isVirtualized = props.isVirtualized,
      disallowEmptySelection = props.disallowEmptySelection,
      _props$selectOnFocus = props.selectOnFocus,
      selectOnFocus = _props$selectOnFocus === void 0 ? false : _props$selectOnFocus,
      disallowTypeAhead = props.disallowTypeAhead,
      shouldUseVirtualFocus = props.shouldUseVirtualFocus; // By default, a KeyboardDelegate is provided which uses the DOM to query layout information (e.g. for page up/page down).
  // When virtualized, the layout object will be passed in as a prop and override this.

  var collator = useCollator({
    usage: 'search',
    sensitivity: 'base'
  });
  var delegate = useMemo(function () {
    return keyboardDelegate || new ListKeyboardDelegate(collection, disabledKeys, ref, collator);
  }, [keyboardDelegate, collection, disabledKeys, ref, collator]); // If not virtualized, scroll the focused element into view when the focusedKey changes.
  // When virtualized, Virtualizer handles this internally.

  useEffect(function () {
    if (!isVirtualized && selectionManager.focusedKey) {
      var element = ref.current.querySelector("[data-key=\"" + selectionManager.focusedKey + "\"]");

      if (element) {
        $a09ba753e08b703267f2392f7fc8e96$var$scrollIntoView(ref.current, element);
      }
    }
  }, [isVirtualized, ref, selectionManager.focusedKey]);

  var _useSelectableCollect = useSelectableCollection({
    ref: ref,
    selectionManager: selectionManager,
    keyboardDelegate: delegate,
    autoFocus: autoFocus,
    shouldFocusWrap: shouldFocusWrap,
    disallowEmptySelection: disallowEmptySelection,
    selectOnFocus: selectOnFocus,
    disallowTypeAhead: disallowTypeAhead,
    shouldUseVirtualFocus: shouldUseVirtualFocus
  }),
      collectionProps = _useSelectableCollect.collectionProps;

  return {
    listProps: collectionProps
  };
}
/**
 * Scrolls `scrollView` so that `element` is visible.
 * Similar to `element.scrollIntoView({block: 'nearest'})` (not supported in Edge),
 * but doesn't affect parents above `scrollView`.
 */

function $a09ba753e08b703267f2392f7fc8e96$var$scrollIntoView(scrollView, element) {
  var offsetX = element.offsetLeft - scrollView.offsetLeft;
  var offsetY = element.offsetTop - scrollView.offsetTop;
  var width = element.offsetWidth;
  var height = element.offsetHeight;
  var x = scrollView.scrollLeft;
  var y = scrollView.scrollTop;
  var maxX = x + scrollView.offsetWidth;
  var maxY = y + scrollView.offsetHeight;

  if (offsetX <= x) {
    x = offsetX;
  } else if (offsetX + width > maxX) {
    x += offsetX + width - maxX;
  }

  if (offsetY <= y) {
    y = offsetY;
  } else if (offsetY + height > maxY) {
    y += offsetY + height - maxY;
  }

  scrollView.scrollLeft = x;
  scrollView.scrollTop = y;
}